import React from 'react';
import { css } from '@emotion/react';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import Nearest from '../../franchise/nearest';
import { colors } from '../../primitives/colors';
import Col from '../../primitives/grid/col';
import Row from '../../primitives/grid/row';
import RowSet from '../../primitives/grid/row-set';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import { breakpoints, borderRadius } from '../../primitives/tokens';
import Typography from '../../primitives/typography';
import Container from '../../primitives/grid/container';
import Image from '../../primitives/image';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import type { HeroProps } from './index';

const styles = {
    section: css({
        position: 'relative',
        marginTop: '28px',
        marginBottom: 'var(--section-margin-bottom)',
        overflow: 'hidden',
        [breakpoints.mb]: {
            marginTop: '0',
        },
    }),
    imageWrapper: css({
        width: '100%',
        height: '100%',
        position: 'relative',
        paddingRight: '30px',
        [breakpoints.tb]: { paddingRight: '0' },
        [breakpoints.mb]: { marginTop: '24px' },
        '&:before': {
            [breakpoints.tb]: { display: 'none' },
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: '0',
            top: '30px',
            zIndex: '-1',
            backgroundPosition: 'right top',
            backgroundImage:
                // eslint-disable-next-line max-len
                "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACIAAAAjCAYAAADxG9hnAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAACxMAAAsTAQCanBgAAAAhdEVYdENyZWF0aW9uIFRpbWUAMjAyMTowNDoyMyAxNjo1MjozOSf3SngAAABJSURBVFhH7dYxDgAQDIXhciirSzmGS1ldiqWTSCMxIP5veR2apuMTANjkNKdSjU1HyaGYu5aVO17zuGseAQAA36KhjWhowKNEOiStEAsIN9AcAAAAAElFTkSuQmCC')",
        },
    }),
    image: css({
        width: '100%',
        height: '100%',
        minHeight: '600px',
        maxHeight: 'var(--max-h-img)',
        objectFit: 'cover',
        overflow: 'hidden',
        transform: 'translateZ(0)',
        [breakpoints.tb]: { minHeight: '480px' },
        [breakpoints.mb]: {
            minHeight: '300px',
            maxHeight: '400px',
        },
    }),
    captionWrap: css({
        textAlign: 'center',
        position: 'absolute',
        bottom: 'var(--caption-bottom-offset)',
        width: '100%',
        padding: '24px',
        paddingRight: 'calc(24px + 32px)',
        [breakpoints.tb]: { paddingRight: '24px' },
        [breakpoints.mb]: { bottom: '0' },
    }),
    captionInner: css({
        backgroundColor: colors.shadesWhite,
        borderRadius: borderRadius.medium,
        width: '100%',
        padding: '12px',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)',
    }),
    contentWrap: css({
        padding: '72px 0',
        [breakpoints.tb]: { padding: '48px 0' },
        [breakpoints.mb]: {
            paddingTop: '32px',
            paddingBottom: '0',
        },
    }),
    buttonWrap: css({
        display: 'flex',
        flexFlow: 'wrap',
        gap: '12px',
    }),
    nearestWrap: css({
        marginTop: '-32px',
        paddingBottom: '32px',
        position: 'relative',
        [breakpoints.tb]: {
            marginTop: '32px',
            paddingBottom: '0',
        },
    }),
    copy: css({
        li: {
            listStyleType: 'var(--list-style-type)',
        },
    }),
};

type SplitHeroProps = React.PropsWithChildren<HeroProps & {
    caption1?: string|null
    caption2?: string|null
}>;

const ImageComponent: React.FC<SplitHeroProps & { showNearest: boolean }> = (props) => {
    const { image, caption1, caption2, showNearest, alignment } = props;
    if (image) {
        return (
            <div
                css={styles.imageWrapper}
                style={{
                    '--max-h-img': alignment === 'center' ? '300px' : 'unset',
                } as React.CSSProperties}
            >
                <Image
                    loading="eager"
                    css={styles.image}
                    image={image}
                />
                {(caption1 || caption2) && (
                    <div
                        css={styles.captionWrap}
                        style={{
                            '--caption-bottom-offset': showNearest ? '28px' : '0',
                        } as React.CSSProperties}
                    >
                        <div css={styles.captionInner}>
                            {caption1 && (
                                <Typography
                                    fontSize={{ dt: 'lg', tb: 'base', mb: 'sm' }}
                                    fontWeight="medium"
                                >
                                    {caption1}
                                </Typography>
                            )}
                            {caption2 && (
                                <Typography
                                    fontSize={{ dt: 'base', tb: 'sm', mb: 'xs' }}
                                >
                                    {caption2}
                                </Typography>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
    return null;
};

const TextComponent: React.FC<SplitHeroProps> = (props) => {
    const {
        heading,
        subheading,
        copy,
        children,
        buttonLink,
        buttonLink2,
        alignment,
    } = props;
    return (
        <RowSet
            breakpoints={{ dt: { between: 32 }, tb: { between: 24 }, mb: { between: 16 } }}
            css={styles.contentWrap}
            style={{
                textAlign: {
                    left: 'left',
                    center: 'center',
                    right: 'left',
                }[(alignment || 'left')],
            } as React.CSSProperties}
        >
            {heading && (
                <Typography
                    as="h1"
                    fontSize={{ dt: '7xl', tb: '5xl', mb: '4xl' }}
                    reducedLetterSpacing
                    dangerouslySetInnerHTML={{ __html: apCaseOnlyTitleTags(heading) }}
                />
            )}
            {subheading && (
                <Typography
                    as="h2"
                    fontSize={{ dt: '4xl', tb: '3xl', mb: '2xl' }}
                    reducedLetterSpacing
                    dangerouslySetInnerHTML={{ __html: apCaseOnlyTitleTags(subheading) }}
                />
            )}
            <Typography
                fontSize={{ dt: '2xl', tb: 'xl', mb: 'lg' }}
                css={[RichTextEditorStyles, styles.copy]}
                style={{
                    '--list-style-type': alignment === 'center' ? 'none' : 'disc',
                } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: copy || '' }}
            />
            {children}
            <div css={styles.buttonWrap}>
                {buttonLink?.title && buttonLink.href && (
                    <StyledButtonAsLink to={buttonLink.href}>
                        {buttonLink.title}
                    </StyledButtonAsLink>
                )}
                {buttonLink2?.title && buttonLink2?.href && (
                    <StyledButtonAsLink to={buttonLink2.href}>
                        {buttonLink2.title}
                    </StyledButtonAsLink>
                )}
            </div>
        </RowSet>
    );
};

const SplitHero = ({ ...props }: SplitHeroProps): JSX.Element => {
    const {
        hasLocationWidget,
        alignment,
    } = props;

    const { isMicrosite } = useMicrosite();
    const showNearest = isMicrosite || hasLocationWidget;

    return (
        <section
            css={styles.section}
            style={{
                '--section-margin-bottom': showNearest ? '0' : '28px',
            } as React.CSSProperties}
        >
            <Container>
                {(alignment === 'left' || !alignment) && (
                    <Row>
                        <Col breakpoints={{ dt: { span: 5, start: 7 }, mb: { span: 4, start: 0 } }}>
                            <ImageComponent {...props} showNearest={!!showNearest} />
                        </Col>
                        <Col breakpoints={{ dt: { span: 6, start: 0 }, tb: { span: 7, start: 0 }, mb: { span: 4, start: 0 } }}>
                            <TextComponent {...props} />
                        </Col>
                    </Row>
                )}
                {(alignment === 'right') && (
                    <Row>
                        <Col breakpoints={{ dt: { span: 6, start: 0 }, mb: { span: 4, start: 0 } }}>
                            <ImageComponent {...props} showNearest={!!showNearest} />
                        </Col>
                        <Col breakpoints={{ dt: { span: 6, start: 6 }, tb: { span: 6, start: 6 }, mb: { span: 4, start: 0 } }}>
                            <TextComponent {...props} />
                        </Col>
                    </Row>
                )}
                {(alignment === 'center') && (
                    <Row>
                        <Col breakpoints={{ dt: { span: 10, start: 1 }, tb: { span: 12, start: 0 }, mb: { span: 4, start: 0 } }}>
                            <ImageComponent {...props} showNearest={!!showNearest} />
                        </Col>
                        <Col breakpoints={{ dt: { span: 10, start: 1 }, tb: { span: 12, start: 0 }, mb: { span: 4, start: 0 } }}>
                            <TextComponent {...props} />
                        </Col>
                    </Row>
                )}
            </Container>
            {showNearest && <div css={styles.nearestWrap}><Nearest /></div>}
        </section>
    );
};

export default SplitHero;
